// ============================================================================
// OBT F3 — İnceleyenin yayın listesi (rol kuyruğundan türetilir)
// ============================================================================
// Backend'de reviewer-scoped "yayın özeti" ucu YOKTUR; bu yüzden özet, rolün
// GERÇEK inceleme kuyruğundan (bm/my/director-queue) yayına göre gruplanarak
// türetilir. Sahte sayaç üretilmez: kuyruk YALNIZ aktörün aksiyon alabileceği
// (submitted/resubmitted) cevapları döndürdüğü için sayaçlar da bu gerçek
// aksiyon-bekleyen kümesini yansıtır. Kampanya üstverisi (başlık/konu/son
// tarih) `GET /obt/campaigns` ile birleştirilir.
//
// Not: revizyon-istenen / incelenen cevaplar tasarım gereği aksiyon kuyruğundan
// ÇIKAR (owner'a veya sonraki kademeye geçer); bu yüzden burada uydurulmaz.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { listBmQueue, listMyQueue, listDirectorQueue, listCampaigns } from '../reviewApi';
import { normalizeReviewError } from '../reviewErrors';

const QUEUE_BY_ROLE = Object.freeze({
  engineer: listBmQueue,
  deputy: listMyQueue,
  director: listDirectorQueue,
});

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

function duePassed(dueAt) {
  if (!dueAt) return false;
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

/** Kuyruk item'larını yayına göre grupla + kampanya üstverisiyle birleştir. */
function groupByPublication(queueItems, campaignById) {
  const groups = new Map();
  for (const item of queueItems) {
    const key = String(item.publication_id);
    if (!groups.has(key)) {
      const camp = campaignById.get(String(item.campaign_id)) || null;
      groups.set(key, {
        publication_id: item.publication_id,
        campaign_id: item.campaign_id,
        campaign_title: item.campaign_title || camp?.title || 'Bilgi Talebi',
        campaign_topic: item.campaign_topic || camp?.topic || null,
        response_due_at: camp?.response_due_at || null,
        due_passed: duePassed(camp?.response_due_at),
        responses: [],
        counts: { actionable: 0, resubmitted: 0, submitted: 0 },
      });
    }
    const g = groups.get(key);
    g.responses.push(item);
    g.counts.actionable += 1;
    if (item.status === 'resubmitted') g.counts.resubmitted += 1;
    if (item.status === 'submitted') g.counts.submitted += 1;
  }
  return Array.from(groups.values()).sort((a, b) =>
    String(a.campaign_title).localeCompare(String(b.campaign_title), 'tr')
  );
}

/**
 * @param {{role: 'engineer'|'deputy'|'director', enabled?: boolean}} options
 */
export function useReviewerPublications({ role, enabled = true } = {}) {
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    publications: [],
  });
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    const fetcher = QUEUE_BY_ROLE[role];
    if (!enabled || !fetcher) {
      setState({ loading: false, error: null, publications: [] });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // Kuyruk BİRİNCİL kaynaktır. Kampanya üstverisi yalnız zenginleştirme
      // (son tarih vb.) içindir; erişilemezse (kapsam/403) yükleme düşmez.
      const [queueItems, campaignPage] = await Promise.all([
        fetcher({}, { signal: controller.signal }),
        listCampaigns({ limit: 200, offset: 0 }, { signal: controller.signal }).catch(
          () => null
        ),
      ]);
      if (controller.signal.aborted) return;
      const campaignById = new Map(
        (Array.isArray(campaignPage?.items) ? campaignPage.items : []).map((c) => [
          String(c.campaign_id),
          c,
        ])
      );
      setState({
        loading: false,
        error: null,
        publications: groupByPublication(queueItems, campaignById),
      });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({ loading: false, error: normalizeReviewError(error), publications: [] });
    }
  }, [role, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { ...state, reload: load };
}
